import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useSupersededOfferingContext } from '../contexts/SupersededOfferingContext';
import { useOfferingSidePanelMatch } from '../hooks/useOfferingSidePanelMatch';
import offeringSidePanelBuySideRouteFactory from '../offeringSidePanelBuySideRouteFactory';
import { OfferingSidePanelRouteContext } from '../OfferingSidePanelContainer';
import ComplianceRoute from './compliance/ComplianceRoute';
import IndicationActivityBuySideRoute from './indication-activity/IndicationActivityBuySideRoute';
import MyInternalDemandRoute from './my-internal-demand/MyInternalDemandRoute';
import UpdateHistoryRoute from './update-history/UpdateHistoryRoute';

/**
 * Side Panel routes for the Buy Side sections of the
 * Order Book module
 */
const OrderBookBuySideRoute: React.FC = () => {
  const baseSidePanelUrl = React.useContext(OfferingSidePanelRouteContext);

  const { isFiledOffering } = useSupersededOfferingContext();

  const sidePanelMatch = useOfferingSidePanelMatch();
  const rest = sidePanelMatch?.rest || '/';

  const redirectUrl =
    baseSidePanelUrl +
    (isFiledOffering
      ? offeringSidePanelBuySideRouteFactory.internalDemand.getUrlPath()
      : offeringSidePanelBuySideRouteFactory.orderBookIndicationActivity.getUrlPath());

  // `rest` isn't a real router location (see useOfferingSidePanelMatch.ts) — `<Routes
  // location={rest}>` matches against it explicitly instead of the actual browser location.
  //
  // This component is itself rendered as the element for OfferingSidePanelBuySideRoutes' own
  // `${orderBook.routePath}/*` <Route>, so react-router has already established "order-book" as
  // the ambient pathnameBase by the time we get here. Even though `rest` above still needs to
  // include that "order-book" prefix (react-router's explicit-location <Routes> requires the
  // given location to start with the ambient pathnameBase), react-router strips that same prefix
  // back off before matching it against the routes below — so those routes must be relative to
  // "order-book", not repeat it, or every match here falls through to the "*" catch-all instead.
  return (
    <Routes location={rest}>
      <Route
        path={`${offeringSidePanelBuySideRouteFactory.orderBookIndicationActivity.routePath.replace(
          offeringSidePanelBuySideRouteFactory.orderBook.routePath,
          ''
        )}/*`}
        element={<IndicationActivityBuySideRoute />}
      />
      <Route
        path={`${offeringSidePanelBuySideRouteFactory.internalDemand.routePath.replace(
          offeringSidePanelBuySideRouteFactory.orderBook.routePath,
          ''
        )}/*`}
        element={<MyInternalDemandRoute />}
      />
      <Route
        path={`${offeringSidePanelBuySideRouteFactory.orderBookUpdateHistory.routePath.replace(
          offeringSidePanelBuySideRouteFactory.orderBook.routePath,
          ''
        )}/*`}
        element={<UpdateHistoryRoute />}
      />
      <Route
        path={`${offeringSidePanelBuySideRouteFactory.orderBookCompliance.routePath.replace(
          offeringSidePanelBuySideRouteFactory.orderBook.routePath,
          ''
        )}/*`}
        element={<ComplianceRoute />}
      />
      <Route path="*" element={<Navigate to={redirectUrl} replace />} />
    </Routes>
  );
};

export default OrderBookBuySideRoute;
